// =============================================================================
// SOCLE DE SÉCURITÉ
// -----------------------------------------------------------------------------
// Toute donnée qui entre dans l'application passe par ici : textes, fichiers,
// liens, mots de passe. Une seule porte d'entrée = un seul endroit à corriger
// le jour où une faille apparaît, et rien qui puisse être oublié dans un coin.
//
// Règle de travail : une nouvelle fonctionnalité qui accepte quoi que ce soit
// de l'utilisateur DOIT utiliser une fonction de ce fichier. L'audit
// (`npm run audit`) le vérifie.
// =============================================================================

/** Erreur destinée à être montrée telle quelle au membre (message en français). */
export class ErreurValidation extends Error {
  constructor(message) {
    super(message);
    this.name = "ErreurValidation";
    this.utilisateur = true; // affichable sans risque
  }
}

/* --------------------------------------------------------------- longueurs */

export const LIMITES = {
  nom: 60,
  bio: 300,
  ville: 60,
  eglise: 60,
  post: 3000,
  commentaire: 1000,
  message: 2000,
  story: 240,
  signalement: 500,
  recherche: 80,
  groupeNom: 60,
  groupeDescription: 400,
  pageNom: 60,
  pageDescription: 600,
  prenom: 40,
  telephone: 20,
  /* DIX, et non huit. Huit caractères se cassent aujourd'hui hors ligne en
     quelques heures dès que la fuite d'un autre site a livré l'empreinte. Deux
     de plus multiplient ce temps par plusieurs milliers, et ne coûtent rien à
     personne. C'est surtout vrai du compte modérateur, qui peut suspendre,
     certifier et lire les attestations de baptême : c'est la cible qui vaut le
     plus cher sur ce réseau. */
  motDePasseMin: 10,
  motDePasseMax: 128,
};

/* ------------------------------------------------------------------ textes */

// Caractères de contrôle et marques de direction : invisibles, ils servent à
// masquer du texte, à falsifier un nom ou à casser un affichage.
const INVISIBLES = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

/**
 * Nettoie et valide un texte saisi par un membre.
 * @returns {string} le texte assaini, prêt à être stocké
 * @throws {ErreurValidation} si vide alors qu'il est requis, ou trop long
 */
export function texte(valeur, max, { champ = "Ce champ", requis = false } = {}) {
  let t = typeof valeur === "string" ? valeur : valeur == null ? "" : String(valeur);
  t = t.replace(INVISIBLES, "");
  t = t.replace(/\r\n?/g, "\n"); // retours à la ligne uniformes
  t = t.replace(/\n{4,}/g, "\n\n\n"); // évite les publications « murs blancs »
  t = t.replace(/[ \t]{4,}/g, "   ");
  t = t.trim();
  if (requis && !t) throw new ErreurValidation(`${champ} est obligatoire.`);
  if (t.length > max) throw new ErreurValidation(`${champ} dépasse la limite de ${max} caractères.`);
  return t;
}

/* ------------------------------------------------------------- le nom réel */

/* ⚠️ LE NOM EST RÉEL — 24 octobre 2026, demandé par Josaphat, « comme sur
   Facebook » : au moins DEUX mots (prénom et nom), en lettres seulement —
   accents et lettres d'ici compris — reliés d'espaces, de traits d'union ou
   d'apostrophes. Ni chiffres, ni symboles, ni croix : « Mr FERNANDO † » ne
   passe plus. Chaque mot fait au moins deux lettres.

   Le format ne PROUVE pas qu'un nom est vrai — ça, c'est l'admission humaine,
   attestation à l'appui — il écarte l'invraisemblable. La même grammaire est
   écrite dans `firestore.rules` (fonction `nomReel`) : qui change l'une
   change l'autre. La cadence « une fois par semestre » vit, elle, dans la
   règle de mise à jour et dans l'enveloppe du magasin. */
/* ⚠️ LETTRES LATINES SEULEMENT — resserré le 24 octobre 2026, demandé par
   Josaphat : un nom en alphabet russe, arabe ou chinois n'est pas un nom
   d'état civil béninois. Accents français et lettres des langues d'ici
   (en orthographe latine) passent ; « João » et « María » aussi — de vrais
   noms d'Agudas. Ce que la grammaire ne PEUT pas trancher : un pseudonyme
   déguisé en lettres latines ordinaires — ça reste le travail du bouton
   Renommer de la modération. */
const MOT_DE_NOM = "[\\p{Script=Latin}][\\p{Script=Latin}'’-]+";
export const NOM_REEL = new RegExp(`^${MOT_DE_NOM}( ${MOT_DE_NOM})+$`, "u");

export function nomReel(valeur) {
  const n = texte(valeur, LIMITES.nom, { champ: "Le nom", requis: true }).replace(/\s+/g, " ");
  if (!NOM_REEL.test(n))
    throw new ErreurValidation(
      "Votre nom doit être votre vrai nom : prénom et nom, en lettres seulement — sans chiffres, symboles ni titres."
    );
  return n;
}

/* ------------------------------------------------------------------- liens */

const SCHEMAS_SURS = ["http:", "https:"];

/**
 * Vérifie qu'une URL est inoffensive avant de l'insérer dans un href/src.
 * Refuse javascript:, vbscript:, file:… et n'accepte les data: que pour les images.
 */
export function lienSur(url) {
  const u = String(url || "").trim();
  if (!u) return "";
  if (/^data:image\/(png|jpe?g|gif|webp);base64,/i.test(u)) return u;
  if (/^data:video\/(mp4|webm|ogg);base64,/i.test(u)) return u;
  // Un document joint en mode local. Le PDF ne s'exécute pas dans la page — il
  // part dans la visionneuse du navigateur — et Chrome refuse de toute façon
  // d'ouvrir une adresse `data:` en premier plan. En ligne, ces adresses sont
  // des https ordinaires : cette ligne ne sert qu'à la démonstration.
  if (/^data:application\/pdf;base64,/i.test(u)) return u;
  if (u.startsWith("blob:")) return u;
  try {
    const parsee = new URL(u, location.href);
    return SCHEMAS_SURS.includes(parsee.protocol) ? parsee.href : "";
  } catch {
    return "";
  }
}

/* ---------------------------------------------------------------- fichiers */

const SIGNATURES = {
  image: [
    { octets: [0xff, 0xd8, 0xff], type: "image/jpeg" },
    { octets: [0x89, 0x50, 0x4e, 0x47], type: "image/png" },
    { octets: [0x47, 0x49, 0x46, 0x38], type: "image/gif" },
    { octets: [0x52, 0x49, 0x46, 0x46], type: "image/webp" }, // RIFF
  ],
  video: [
    { decalage: 4, octets: [0x66, 0x74, 0x79, 0x70], type: "video/mp4" }, // ftyp
    { octets: [0x1a, 0x45, 0xdf, 0xa3], type: "video/webm" },
  ],
  // Documents joints à un message. Le PDF, et lui seul : il commence par
  // « %PDF », une signature qu'on peut vérifier. Word et Excel sont des
  // archives ZIP, indistinguables d'un .zip ou d'un .apk aux premiers octets —
  // les accepter reviendrait à accepter n'importe quoi.
  application: [
    { octets: [0x25, 0x50, 0x44, 0x46], type: "application/pdf" }, // %PDF
  ],
  // Un message vocal n'est pas choisi dans la galerie : il sort de
  // `MediaRecorder`, qui rend du webm sur Android et du mp4 sur iPhone. On
  // vérifie quand même les premiers octets — la règle vaut mieux que l'exception.
  audio: [
    { octets: [0x1a, 0x45, 0xdf, 0xa3], type: "audio/webm" },
    { decalage: 4, octets: [0x66, 0x74, 0x79, 0x70], type: "audio/mp4" },
    { octets: [0x4f, 0x67, 0x67, 0x53], type: "audio/ogg" }, // OggS
    { octets: [0x49, 0x44, 0x33], type: "audio/mpeg" }, // ID3
    { octets: [0xff, 0xfb], type: "audio/mpeg" },
  ],
};

const NOM_TYPE = { image: "image", video: "vidéo", audio: "enregistrement", application: "document PDF" };

/**
 * Contrôle un fichier AVANT envoi : type annoncé, taille, et surtout les
 * premiers octets réels — un fichier renommé en .jpg ne passe pas.
 */
export async function fichierSur(fichier, type, maxMo) {
  if (!fichier) throw new ErreurValidation("Aucun fichier sélectionné.");
  if (!fichier.type.startsWith(type + "/"))
    throw new ErreurValidation(`Ce fichier n'est pas un(e) ${NOM_TYPE[type] || type}.`);
  if (fichier.size > maxMo * 1024 * 1024)
    throw new ErreurValidation(`Fichier trop lourd : ${Math.round(fichier.size / 1048576)} Mo pour un maximum de ${maxMo} Mo.`);
  if (fichier.size === 0) throw new ErreurValidation("Ce fichier est vide.");

  const debut = new Uint8Array(await fichier.slice(0, 12).arrayBuffer());
  const reconnu = (SIGNATURES[type] || []).some(({ octets, decalage = 0 }) =>
    octets.every((o, i) => debut[decalage + i] === o)
  );
  if (!reconnu)
    throw new ErreurValidation(`Ce fichier ne ressemble pas à un(e) ${NOM_TYPE[type] || type} valide.`);
  return true;
}

/* ------------------------------------------------------------ mots de passe */

/* ⚠️ TOUS FONT DIX CARACTÈRES OU PLUS, et c'est le point.
   La liste d'avant — « password », « jesus123 », « azertyui » — ne servait plus
   à rien depuis que le minimum est passé à dix : ces mots-là tombent sur la
   LONGUEUR, avant d'arriver ici. Une liste qui ne rejette que ce qui est déjà
   rejeté donne l'illusion d'une protection.
   Ceux-ci passeraient la longueur. Ils sont choisis pour CE réseau : un
   attaquant qui vise une église essaie « jesuschrist » avant « qwertyuiop ». */
const MOTS_DE_PASSE_FAIBLES = new Set([
  "1234567890", "0123456789", "azertyuiop", "qwertyuiop", "motdepasse",
  "motdepasse1", "password12", "passwordvvv", "1111111111", "aaaaaaaaaa",
  "koinonia12", "koinonia123", "jesuschrist", "jesus12345", "alleluia12",
  "alleluia123", "dieuestbon", "emmanuel12", "amenamenamen", "gloireadieu",
]);

/** Refuse les mots de passe trop courts, trop simples ou trop connus. */
export function motDePasseSur(mdp) {
  const m = String(mdp || "");
  if (m.length < LIMITES.motDePasseMin)
    throw new ErreurValidation(`Le mot de passe doit faire au moins ${LIMITES.motDePasseMin} caractères.`);
  if (m.length > LIMITES.motDePasseMax) throw new ErreurValidation("Mot de passe trop long.");
  if (MOTS_DE_PASSE_FAIBLES.has(m.toLowerCase()))
    throw new ErreurValidation("Ce mot de passe est trop courant. Choisissez-en un autre.");
  if (/^(.)\1+$/.test(m)) throw new ErreurValidation("Un mot de passe ne peut pas être une seule lettre répétée.");
  return m;
}

/** Adresse e-mail : format simple et longueur bornée. */
export function emailSur(email) {
  const e = String(email || "").trim().toLowerCase();
  if (e.length > 254 || !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(e))
    throw new ErreurValidation("Adresse e-mail invalide.");
  return e;
}

/* -------------------------------------------------- identité à l'inscription */

export const AGE_MINIMUM = 13;

/**
 * Date de naissance : format ISO, date réelle, ni dans le futur ni absurde,
 * et âge minimum — comme le font les grands réseaux.
 */
export function dateNaissanceSure(valeur) {
  const t = String(valeur || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) throw new ErreurValidation("Indiquez votre date de naissance.");
  const d = new Date(t + "T00:00:00");
  if (Number.isNaN(d.getTime())) throw new ErreurValidation("Cette date n'existe pas.");
  const maintenant = new Date();
  if (d > maintenant) throw new ErreurValidation("La date de naissance ne peut pas être dans le futur.");
  let age = maintenant.getFullYear() - d.getFullYear();
  const m = maintenant.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && maintenant.getDate() < d.getDate())) age--;
  if (age > 120) throw new ErreurValidation("Vérifiez votre date de naissance.");
  if (age < AGE_MINIMUM) throw new ErreurValidation(`Il faut avoir au moins ${AGE_MINIMUM} ans pour rejoindre Koinonia.`);
  return t;
}

export const GENRES = ["femme", "homme", "autre"];

export function genreSur(valeur) {
  const g = String(valeur || "").trim().toLowerCase();
  if (!GENRES.includes(g)) throw new ErreurValidation("Indiquez votre genre.");
  return g;
}

/**
 * Numéro de téléphone : on ne garde que les chiffres et le « + » de tête.
 * Volontairement souple — les formats varient d'un pays à l'autre.
 */
export function telephoneSur(valeur, { requis = false } = {}) {
  const brut = String(valeur || "").trim();
  if (!brut) {
    if (requis) throw new ErreurValidation("Indiquez un numéro de téléphone.");
    return "";
  }
  const t = brut.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  if (!/^\+?\d{8,15}$/.test(t)) throw new ErreurValidation("Numéro de téléphone invalide.");
  return t;
}

/* ------------------------------------------------------- confidentialité */

/**
 * Les réglages de confidentialité d'un membre.
 *
 * Les valeurs par défaut reproduisent exactement le comportement d'avant leur
 * existence : un compte ouvert. C'est délibéré — une mise à jour ne doit jamais
 * refermer en silence le profil de quelqu'un qui n'a rien demandé. Le membre
 * choisit de se fermer, jamais l'inverse.
 */
export const CONFIDENTIALITE = {
  publications: { valeurs: ["tous", "amis"], defaut: "tous" },
  demandes: { valeurs: ["tous", "amisDAmis"], defaut: "tous" },
  messages: { valeurs: ["tous", "amis"], defaut: "tous" },
  details: { valeurs: ["tous", "amis"], defaut: "tous" },
  annuaire: { valeurs: [true, false], defaut: true },
  presence: { valeurs: [true, false], defaut: true },
  /* ⚠️ RETOURNÉ LE 24 OCTOBRE 2026, décidé par Josaphat : « tout le monde
     doit pouvoir avoir des abonnés ». Depuis la onzième passe, s'abonner est
     LA clé qui ouvre les publications publiques d'un non-ami — un réglage
     fermé par défaut aurait rendu la communauté illisible. Le silence vaut
     donc désormais accord, et le membre garde la liberté de FERMER dans ses
     Paramètres (la règle serveur lit la même clé, avec le même défaut). */
  abonnes: { valeurs: [true, false], defaut: true },
  /* ⚠️ L'ANNIVERSAIRE PART ALLUMÉ, ET CE N'EST PAS UNE DISTRACTION.
     La règle voisine dit que ce qui est NEUF ne s'active pas dans le dos de
     quelqu'un — c'est pourquoi `abonnes` est éteint. L'anniversaire n'est pas
     du même ordre, et il faut savoir pourquoi :

       · `abonnes` ouvre une RELATION nouvelle — être suivi par des gens qui ne
         sont pas ses amis. C'est un changement de nature.
       · l'anniversaire ne publie qu'un FAIT, et le plus mince qui soit : le
         jour et le mois. Jamais l'année, donc jamais l'âge. La date complète
         reste où l'audit l'a mise, dans `prive/contact`.

     Et la communauté n'est pas ouverte : on n'y entre qu'après validation.
     Éteint par défaut, ce réglage ferait une fonction que personne n'active et
     donc un anniversaire que personne ne souhaite — l'inverse de ce qu'on
     cherche. Le membre peut l'éteindre d'un geste dans ses paramètres. */
  anniversaire: { valeurs: [true, false], defaut: true },
};

export const CONFIDENTIALITE_DEFAUT = Object.fromEntries(
  Object.entries(CONFIDENTIALITE).map(([cle, r]) => [cle, r.defaut])
);

/**
 * Valide un bloc de réglages. Toute clé inconnue est écartée, toute valeur
 * hors liste retombe sur le défaut : un réglage inventé ne peut pas servir à
 * glisser autre chose dans le profil.
 */
export function confidentialiteSure(valeur) {
  const entree = valeur && typeof valeur === "object" ? valeur : {};
  const sortie = {};
  for (const [cle, regle] of Object.entries(CONFIDENTIALITE)) {
    const v = entree[cle];
    sortie[cle] = regle.valeurs.includes(v) ? v : regle.defaut;
  }
  return sortie;
}

/** Lecture tolérante : un profil ancien n'a pas encore le bloc. */
export const confidentialiteDe = (profil) => confidentialiteSure(profil?.confidentialite);

/* ----------------------------------------------- empreinte de mot de passe */

const encodeur = new TextEncoder();

/** Sel aléatoire en hexadécimal. */
export function sel(octets = 16) {
  return [...crypto.getRandomValues(new Uint8Array(octets))].map((o) => o.toString(16).padStart(2, "0")).join("");
}

/**
 * Empreinte PBKDF2-SHA256 (150 000 tours). Utilisée par le mode démo local :
 * aucun mot de passe n'est stocké en clair, même sur cet appareil.
 * En mode Firebase, c'est Firebase Auth qui s'en charge et ceci ne sert pas.
 */
export async function empreinte(motDePasse, selHex) {
  const cle = await crypto.subtle.importKey("raw", encodeur.encode(motDePasse), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: encodeur.encode(selHex), iterations: 150000, hash: "SHA-256" },
    cle,
    256
  );
  return [...new Uint8Array(bits)].map((o) => o.toString(16).padStart(2, "0")).join("");
}

/** Comparaison à durée constante : ne révèle pas où deux empreintes diffèrent. */
export function memeEmpreinte(a, b) {
  const x = String(a || "");
  const y = String(b || "");
  if (x.length !== y.length) return false;
  let diff = 0;
  for (let i = 0; i < x.length; i++) diff |= x.charCodeAt(i) ^ y.charCodeAt(i);
  return diff === 0;
}

/* ------------------------------------------------------------- anti-abus */

const CLE_LIMITES = "kn.limites";

/**
 * Garde-fou anti-spam côté appareil : au plus `max` actions par fenêtre.
 * ⚠️ C'est un frein, pas une protection : un attaquant peut le contourner.
 * La vraie limite doit être posée côté serveur (voir SECURITE.md § À venir).
 */
export function limiter(cle, max, fenetreMs) {
  let tout = {};
  try {
    tout = JSON.parse(localStorage.getItem(CLE_LIMITES) || "{}");
  } catch {
    tout = {};
  }
  const maintenant = Date.now();
  const recentes = (tout[cle] || []).filter((t) => maintenant - t < fenetreMs);
  if (recentes.length >= max) {
    const attente = Math.ceil((fenetreMs - (maintenant - recentes[0])) / 1000);
    throw new ErreurValidation(`Vous allez trop vite. Réessayez dans ${attente} seconde${attente > 1 ? "s" : ""}.`);
  }
  recentes.push(maintenant);
  tout[cle] = recentes;
  try {
    localStorage.setItem(CLE_LIMITES, JSON.stringify(tout));
  } catch {
    /* mémoire pleine : on n'empêche pas l'action pour autant */
  }
  return true;
}

/** Efface un compteur : appelé après une réussite, pour ne pas punir les erreurs de frappe. */
export function oublier(cle) {
  try {
    const tout = JSON.parse(localStorage.getItem(CLE_LIMITES) || "{}");
    delete tout[cle];
    localStorage.setItem(CLE_LIMITES, JSON.stringify(tout));
  } catch {
    /* rien à faire */
  }
}

/** Cadences autorisées, au même endroit pour être relues d'un coup d'œil. */
export const CADENCES = {
  publication: [8, 60_000], // 8 publications par minute
  commentaire: [20, 60_000],
  message: [40, 60_000],
  story: [10, 300_000], // 10 statuts par 5 minutes
  signalement: [10, 600_000],
  groupe: [5, 3_600_000], // 5 groupes créés par heure
  page: [3, 3_600_000],   // 3 pages créées par heure
  connexion: [10, 300_000], // 10 tentatives de connexion par 5 minutes
  inscription: [5, 3_600_000], // 5 comptes par heure et par appareil
};

/** Applique une cadence déclarée dans CADENCES. */
export function cadence(nom) {
  const [max, fenetre] = CADENCES[nom] || [];
  if (max) limiter(nom, max, fenetre);
}
